import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import axios from '../api/axiosConfig';
import { AuthContext } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import './Soportes.css';

const API = '/api/support';

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-CO', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

// ─── Create Modal ─────────────────────────────────────────────────────────────
const CreateModal = ({ users, onClose, onCreated }) => {
  const [form, setForm] = useState({ title: '', description: '', target_user_id: '' });
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const handleImage = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setImageFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const removeImage = () => { setImageFile(null); setPreview(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('El título es obligatorio'); return; }
    setLoading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('description', form.description);
      if (form.target_user_id) fd.append('target_user_id', form.target_user_id);
      if (imageFile) fd.append('image', imageFile);
      await axios.post(API, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onCreated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al crear el soporte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="support-modal-overlay" onClick={onClose}>
      <div className="support-modal" onClick={(e) => e.stopPropagation()}>
        <div className="support-modal-header">
          <h2>🎫 Nuevo Soporte</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="support-modal-body">
            {error && <div className="s-error-banner">⚠️ {error}</div>}
            <div className="s-form-group">
              <label>Título <span className="required">*</span></label>
              <input
                type="text" placeholder="Describe brevemente el soporte..."
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                disabled={loading}
              />
            </div>
            <div className="s-form-group">
              <label>Usuario Destinatario</label>
              <select
                value={form.target_user_id}
                onChange={(e) => setForm({ ...form, target_user_id: e.target.value })}
                disabled={loading}
              >
                <option value="">— Sin usuario específico —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.username} ({u.role})</option>
                ))}
              </select>
            </div>
            <div className="s-form-group">
              <label>Observación / Descripción</label>
              <textarea
                placeholder="Detalla el soporte, los pasos realizados o la información relevante..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                disabled={loading} rows={4}
              />
            </div>
            <div className="s-form-group">
              <label>Imagen de Referencia</label>
              {!preview ? (
                <div className="image-upload-area" onClick={() => fileRef.current?.click()}>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleImage} />
                  <div className="upload-icon">🖼️</div>
                  <div className="upload-text">Haz clic para subir imagen</div>
                  <div className="upload-hint">JPEG, PNG, GIF, WebP — máx. 10 MB</div>
                </div>
              ) : (
                <div className="image-preview-wrap">
                  <img src={preview} alt="preview" className="image-preview" />
                  <button type="button" className="remove-image-btn" onClick={removeImage}>×</button>
                </div>
              )}
            </div>
          </div>
          <div className="support-modal-footer">
            <button type="button" className="btn-modal-cancel" onClick={onClose} disabled={loading}>Cancelar</button>
            <button type="submit" className="btn-modal-submit" disabled={loading}>
              {loading ? '⏳ Guardando...' : '✅ Crear Soporte'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Resolve Modal ────────────────────────────────────────────────────────────
const ResolveModal = ({ ticket, onClose, onResolved }) => {
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  const handleResolve = async () => {
    setLoading(true);
    try {
      await axios.patch(`${API}/${ticket.id}/resolve`, { resolution_note: note });
      onResolved();
      onClose();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al resolver el ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="support-modal-overlay" onClick={onClose}>
      <div className="support-modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <div className="support-modal-header">
          <h2>✅ Resolver Soporte</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="support-modal-body">
          <p style={{ color: '#475569', fontSize: 14, marginBottom: 16 }}>
            ¿Marcar <strong>"{ticket.title}"</strong> como resuelto?
          </p>
          <div className="s-form-group">
            <label>Nota de Resolución (opcional)</label>
            <textarea
              placeholder="Describe qué se hizo para resolver el soporte..."
              value={note} onChange={(e) => setNote(e.target.value)} rows={4}
            />
          </div>
        </div>
        <div className="support-modal-footer">
          <button className="btn-modal-cancel" onClick={onClose} disabled={loading}>Cancelar</button>
          <button className="btn-modal-resolve" onClick={handleResolve} disabled={loading}>
            {loading ? '⏳ Procesando...' : '✅ Marcar como Resuelto'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Comments Modal ───────────────────────────────────────────────────────────
const CommentsModal = ({ ticket, onClose, user }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const scrollRef = useRef(null);

  const fetchComments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/${ticket.id}/comments`);
      setComments(res.data);
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setFetching(false);
    }
  }, [ticket.id]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/${ticket.id}/comments`, { comment: newComment });
      setComments([...comments, res.data]);
      setNewComment('');
    } catch (err) {
      alert('Error al enviar el comentario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="support-modal-overlay" onClick={onClose}>
      <div className="support-modal" onClick={(e) => e.stopPropagation()}>
        <div className="support-modal-header">
          <h2>💬 Comentarios: {ticket.title}</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="comments-chat-body">
          <div className="comments-list" ref={scrollRef}>
            {fetching ? (
              <div className="loading-state" style={{ padding: '20px' }}>⏳ Cargando...</div>
            ) : comments.length === 0 ? (
              <div className="empty-state" style={{ padding: '20px' }}>
                <div className="empty-icon">💭</div>
                <p>No hay comentarios aún. Escribe el primero.</p>
              </div>
            ) : (
              comments.map(c => {
                const isMine = c.user_id === user.id;
                return (
                  <div key={c.id} className={`comment-bubble-wrapper ${isMine ? 'mine' : 'other'}`}>
                    <div className={`comment-bubble ${isMine ? 'mine' : 'other'}`}>
                      <div className="comment-meta">
                        <span className="author">{isMine ? 'Tú' : c.username}</span>
                        <span className="date">{fmtDate(c.created_at)}</span>
                      </div>
                      <div className="comment-text">{c.comment}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {!ticket.is_resolved && (
            <form className="comments-input-area" onSubmit={handleSend}>
              <input 
                type="text" 
                placeholder="Escribe un comentario..." 
                value={newComment} 
                onChange={(e) => setNewComment(e.target.value)}
                disabled={loading}
              />
              <button type="submit" className="btn-send-comment" disabled={loading || !newComment.trim()}>
                ➤
              </button>
            </form>
          )}
          {ticket.is_resolved && (
            <div style={{ padding: '15px', textAlign: 'center', background: '#fef2f2', color: '#b91c1c', fontSize: '13px', fontWeight: '500' }}>
              🔒 Este ticket está resuelto. No se pueden enviar más comentarios.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Lightbox ─────────────────────────────────────────────────────────────────
const Lightbox = ({ src, onClose }) => (
  <div className="lightbox-overlay" onClick={onClose}>
    <img src={src} alt="Referencia" className="lightbox-img" />
  </div>
);

// ─── Ticket Card ──────────────────────────────────────────────────────────────
const TicketCard = ({ ticket, isSuperAdmin, onResolve, onUnresolve, onDelete, onImageClick }) => {
  const imgSrc = ticket.image_url ? `${axios.defaults.baseURL || ''}${ticket.image_url}` : null;

  return (
    <div className={`ticket-card ${ticket.is_resolved ? 'resolved' : 'pending'}`}>
      <div className="ticket-card-inner">
        <div className="ticket-main">
          <div className="ticket-top-row">
            <span className={`ticket-status-badge ${ticket.is_resolved ? 'resolved' : 'pending'}`}>
              {ticket.is_resolved ? '✅ Resuelto' : '🕐 Pendiente'}
            </span>
          </div>
          <h3 className="ticket-title">{ticket.title}</h3>
          <div className="ticket-meta">
            {ticket.target_user_name && (
              <span>👤 Para: <strong>{ticket.target_user_name}</strong></span>
            )}
            {isSuperAdmin && ticket.created_by_name && (
              <span>🔐 Por: {ticket.created_by_name}</span>
            )}
            <span>📅 {fmtDate(ticket.created_at)}</span>
            {ticket.is_resolved && ticket.resolved_at && (
              <span>✅ Resuelto: {fmtDate(ticket.resolved_at)}</span>
            )}
          </div>
          {ticket.description && (
            <p className="ticket-description">{ticket.description}</p>
          )}
          {ticket.is_resolved && ticket.resolution_note && (
            <div className="ticket-resolution-note">
              <strong>📋 Nota de resolución</strong>
              {ticket.resolution_note}
            </div>
          )}
        </div>
        {imgSrc && (
          <div className="ticket-image-col">
            <img
              src={imgSrc} alt="Referencia" className="ticket-image-thumb"
              onClick={() => onImageClick(imgSrc)} title="Ver imagen completa"
            />
          </div>
        )}
      </div>
      <div className="ticket-actions" style={{ justifyContent: isSuperAdmin ? 'space-between' : 'flex-end' }}>
        <button className="btn-comments" onClick={() => onResolve(ticket, true)}>
          💬 Comentarios {ticket.comments_count > 0 ? `(${ticket.comments_count})` : ''}
        </button>
        {isSuperAdmin && (
          <div style={{ display: 'flex', gap: '8px' }}>
            {!ticket.is_resolved ? (
              <button className="btn-resolve" onClick={() => onResolve(ticket, false)}>✅ Marcar Resuelto</button>
            ) : (
              <button className="btn-unresolve" onClick={() => onUnresolve(ticket.id)}>🔄 Reabrir</button>
            )}
            <button className="btn-delete-ticket" onClick={() => onDelete(ticket.id)}>🗑️ Eliminar</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const Soportes = () => {
  const { user } = useContext(AuthContext);
  const isSuperAdmin = user?.role === 'superadmin';

  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('todos');
  const [showCreate, setShowCreate] = useState(false);
  const [resolveTicket, setResolveTicket] = useState(null);
  const [commentsTicket, setCommentsTicket] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API);
      setTickets(res.data);
    } catch (err) {
      console.error('Error loading tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
    if (isSuperAdmin) {
      axios.get(`${API}/users`).then((r) => setUsers(r.data)).catch(() => {});
    }
  }, [isSuperAdmin]);

  const handleUnresolve = async (id) => {
    if (!window.confirm('¿Reabrir este ticket como pendiente?')) return;
    try {
      await axios.patch(`${API}/${id}/unresolve`);
      loadTickets();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al reabrir');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este ticket? No se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/${id}`);
      loadTickets();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al eliminar');
    }
  };

  const filtered = tickets.filter((t) => {
    if (filter === 'pendiente') return !t.is_resolved;
    if (filter === 'resuelto') return t.is_resolved;
    return true;
  });

  const total = tickets.length;
  const resueltos = tickets.filter((t) => t.is_resolved).length;
  const pendientes = total - resueltos;

  const newlyResolved = !isSuperAdmin
    ? tickets.filter((t) => t.is_resolved &&
        new Date(t.resolved_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))
    : [];

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Navbar title="🎫 Soportes" />

        <div className="page-content">

          {/* Header */}
          <div className="soportes-header">
            <div className="soportes-title-block">
              <h1>🎫 {isSuperAdmin ? 'Gestión de Soportes' : 'Mis Soportes'}</h1>
              <p>
                {isSuperAdmin
                  ? 'Crea y gestiona tickets de soporte para los usuarios del sistema'
                  : 'Aquí puedes ver los soportes que el administrador ha creado para ti'}
              </p>
            </div>
            {isSuperAdmin && (
              <button className="btn-nuevo-soporte" onClick={() => setShowCreate(true)}>
                ＋ Nuevo Soporte
              </button>
            )}
          </div>

          {/* Notification bar for users */}
          {!isSuperAdmin && newlyResolved.length > 0 && (
            <div className="resolved-notification-bar">
              🎉 &nbsp;
              {newlyResolved.length === 1
                ? '¡Un requerimiento tuyo fue atendido y resuelto esta semana!'
                : `¡${newlyResolved.length} requerimientos tuyos fueron resueltos esta semana!`}
            </div>
          )}

          {/* Stats (superadmin only) */}
          {isSuperAdmin && (
            <div className="soportes-stats">
              <div className="stat-card">
                <div className="stat-icon purple">🎫</div>
                <div className="stat-info">
                  <div className="stat-value">{total}</div>
                  <div className="stat-label">Total</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon amber">🕐</div>
                <div className="stat-info">
                  <div className="stat-value">{pendientes}</div>
                  <div className="stat-label">Pendientes</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon green">✅</div>
                <div className="stat-info">
                  <div className="stat-value">{resueltos}</div>
                  <div className="stat-label">Resueltos</div>
                </div>
              </div>
            </div>
          )}

          {/* Filter tabs */}
          <div className="soportes-tabs">
            {[
              { key: 'todos', label: `📋 Todos (${total})` },
              { key: 'pendiente', label: `🕐 Pendientes (${pendientes})` },
              { key: 'resuelto', label: `✅ Resueltos (${resueltos})` },
            ].map(({ key, label }) => (
              <button
                key={key}
                className={`tab-btn ${filter === key ? 'active' : ''}`}
                onClick={() => setFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Content */}
          {loading ? (
            <div className="loading-state">⏳ Cargando soportes...</div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                {filter === 'resuelto' ? '🎉' : filter === 'pendiente' ? '☕' : '🎫'}
              </div>
              <h3>
                {filter === 'todos' ? 'No hay tickets de soporte aún'
                  : filter === 'pendiente' ? 'Sin tickets pendientes'
                  : 'Sin tickets resueltos'}
              </h3>
              <p>
                {isSuperAdmin && filter === 'todos'
                  ? 'Crea el primero con el botón "Nuevo Soporte"'
                  : 'Cuando haya actividad aparecerá aquí'}
              </p>
            </div>
          ) : (
            <div className="tickets-grid">
              {filtered.map((t) => (
                <TicketCard
                  key={t.id}
                  ticket={t}
                  isSuperAdmin={isSuperAdmin}
                  onResolve={(tk, isComments) => {
                    if (isComments) setCommentsTicket(tk);
                    else setResolveTicket(tk);
                  }}
                  onUnresolve={handleUnresolve}
                  onDelete={handleDelete}
                  onImageClick={setLightboxSrc}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreate && (
        <CreateModal users={users} onClose={() => setShowCreate(false)} onCreated={loadTickets} />
      )}
      {resolveTicket && (
        <ResolveModal
          ticket={resolveTicket}
          onClose={() => setResolveTicket(null)}
          onResolved={loadTickets}
        />
      )}
      {commentsTicket && (
        <CommentsModal 
          ticket={commentsTicket}
          onClose={() => setCommentsTicket(null)}
          user={user}
        />
      )}
      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </div>
  );
};

export default Soportes;
